import {
  NumberProvider,
  PhoneNumberType,
} from '#/shared/api/generated/communication/models'
import type { MyLine } from '#/shared/api/generated/communication/models'

// A line's eligibility for an action is the backend-computed capability flag —
// `outbound_sms` / `outbound_call` already fold in carrier support and mainline
// membership rules, so the UI just reads the flag.
export const LINE_CAPABILITY = {
  sms: 'sms',
  call: 'call',
} as const

export type LineCapability =
  (typeof LINE_CAPABILITY)[keyof typeof LINE_CAPABILITY]

/**
 * Whether a line's gateway is the premium messaging gateway. `"speko"` is a
 * stored wire value the backend deliberately kept — treat it as meaning
 * Premium. Since 2026-07-28 premium lines are voice-capable and can be
 * mainline/sharedline/transfer targets like any other line; the one remaining
 * difference is MMS: the backend silently drops `media_urls`, so hide the
 * media affordances. Absent `provider` means Telnyx.
 */
export function isImessageProvider(provider?: NumberProvider): boolean {
  return provider === NumberProvider.speko
}

/**
 * Whether this line may be set as the account default. Mainlines are
 * membership-based and the backend rejects setting them default (400) — pick
 * such a line as the active outbound line instead of persisting it.
 */
export function canBeDefaultLine(line: MyLine): boolean {
  return line.type !== PhoneNumberType.mainline
}

/**
 * Whether a number type is a shared line — mainline or sharedline. Both are
 * membership-based: multiple users are assigned via members (with per-channel
 * inbound/outbound toggles), unlike a personal number's single owner.
 */
export function isSharedNumberType(type: PhoneNumberType): boolean {
  return (
    type === PhoneNumberType.mainline || type === PhoneNumberType.sharedline
  )
}

/** Whether this user may originate the given action from the line. */
export function canUse(line: MyLine, capability: LineCapability): boolean {
  return capability === LINE_CAPABILITY.sms
    ? line.outbound_sms
    : line.outbound_call
}

/** Default line first, rest in original order. */
function defaultFirst(lines: MyLine[]): MyLine[] {
  return [
    ...lines.filter((line) => line.is_default),
    ...lines.filter((line) => !line.is_default),
  ]
}

/** Lines eligible for the action, default line first. */
export function outboundLines(
  lines: MyLine[],
  capability: LineCapability,
): MyLine[] {
  return defaultFirst(lines.filter((line) => canUse(line, capability)))
}

/** Lines usable for any outbound action (call or text), default line first. */
export function outboundCapableLines(lines: MyLine[]): MyLine[] {
  return defaultFirst(
    lines.filter((line) => line.outbound_sms || line.outbound_call),
  )
}
